import { useEffect, useState } from "react";
import { toast } from "sonner";
import { toReviewItems, isCustomKey } from "./aiReview";
import { addCustomExample } from "./aiCustom";
import { sendAiFeedback, feedbackItem, locationOf } from "./aiLearning";
import { placeByName, moveTo } from "./aiInsert";

// Shared AI review state: used by the floating AI panel and by the PDF overlay, so every change is live in both.
export function useAiReview(inItems, projectId, drawingType, t) {
    const [items, setItems] = useState([]);
    const [markFor, setMarkFor] = useState(null);
    const [pendingMove, setPendingMove] = useState(null);
    const [chain, setChain] = useState(null);
    const [focusRow, setFocusRow] = useState(null);
    const [flashBox, setFlashBox] = useState(null);
    const [markStay, setMarkStayS] = useState(() => localStorage.getItem("ai_mark_stay") === "1");
    const setMarkStay = (v) => { localStorage.setItem("ai_mark_stay", v ? "1" : "0"); setMarkStayS(v); };
    const [markMode, setMarkModeS] = useState(() => localStorage.getItem("ai_mark_mode") || "free");
    const setMarkMode = (v) => { localStorage.setItem("ai_mark_mode", v); setMarkModeS(v); };
    useEffect(() => { setItems(toReviewItems(inItems)); }, [inItems]);
    // Undo (Ctrl+Z) of a move: drop fixes whose value is no longer at the target, so AI never gets undone corrections.
    useEffect(() => {
        setChain((c) => {
            if (!c) return c;
            const fixes = c.fixes.filter((x) => (x.kind === "reorder"
                ? (() => { const i = items.findIndex((it) => it._id === x.id); return i >= 0 && (x.afterId ? items[i - 1]?._id === x.afterId : i === 0); })()
                : items.some((it) => String(it.f.name?.value ?? "") === x.to && String(it.f[x.field]?.value ?? "") === x.value)));
            if (fixes.length === c.fixes.length) return c;
            return fixes.length ? { ...c, fixes } : null;
        });
    }, [items]);
    const patchF = (id, key, p) => setItems((list) => list.map((it) => (it._id !== id ? it : { ...it, f: { ...it.f, [key]: { ...it.f[key], ...p } } })));
    const send = (it, key, kind, value, extra) => sendAiFeedback(projectId, [{ ...feedbackItem(it, key, kind, value), ...extra }], drawingType);

    const reject = (it, key, value) => {
        patchF(it._id, key, { value, edited: true, fb: "fixed" });
        send(it, key, "correct", value);
        toast.success(t("al_learned_toast"));
    };
    const confirm = (it, key) => {
        const f = it.f[key] || {};
        if (f.edited && String(f.value ?? "").trim() !== String(f.orig ?? "").trim() && f.orig !== "" && f.orig != null) { reject(it, key, f.value); return; }
        patchF(it._id, key, { fb: "ok", conf: 1, learned: null });
        send(it, key, "confirm", f.value);
    };
    const remove = (it, key) => {
        const f = it.f[key] || {};
        if (f.orig !== "" && f.orig != null && !f.manual) send(it, key, "remove", null, { ai_value: String(f.value ?? "") });
        patchF(it._id, key, { value: key === "qty" ? 1 : "", box: null, conf: 0, edited: true, fb: "removed", learned: null });
        toast.success(t("ar_removed"));
    };
    const reassign = (it, from, to) => {
        const f = it.f[from] || {};
        if (from === to) return;
        if (!f.manual) send(it, from, "reassign", f.value, { ai_value: String(f.value ?? ""), reassign_to: to });
        setItems((list) => list.map((x) => (x._id !== it._id ? x : {
            ...x, f: {
                ...x.f,
                [to]: { ...x.f[to], value: f.value, box: f.box, conf: 1, edited: true, fb: "fixed", learned: null },
                // swap: the previous value of the target field moves to the source field
                [from]: x.f[to]?.value !== "" && x.f[to]?.value != null && !(to === "qty" && !x.f[to]?.box)
                    ? { ...x.f[from], value: x.f[to].value, box: x.f[to].box, conf: x.f[to].conf, edited: true, fb: null, learned: null }
                    : { ...x.f[from], value: from === "qty" ? 1 : "", box: null, conf: 0, edited: true, fb: "removed", learned: null },
            },
        })));
        toast.success(t("ar_reassigned", { f: t(`ai_f_${to}`) }));
    };
    // Region marked by the user: itemId null = new row.
    const addRegion = ({ itemId, key, value, box, page, newName, context }) => {
        let target = items.find((x) => x._id === itemId);
        if (!target) {
            const _id = Math.max(-1, ...items.map((x) => x._id)) + 1;
            const base = toReviewItems([{ page }])[0];
            base.f.qty = { ...base.f.qty, conf: 1, edited: true };
            if (newName && key !== "name") base.f.name = { value: newName, box: null, conf: 1, edited: true, fb: "fixed", manual: true };
            target = { ...base, _id, page, manual: true, group: items.find((x) => x.page === page)?.group, block: items.find((x) => x.page === page)?.block || "" };
            setItems((list) => [...list, target]);
        }
        const id = target._id;
        const fPage = target.page !== page ? page : undefined;
        setItems((list) => {
            const next = list.map((x) => (x._id !== id ? x : {
                ...x, pages: fPage ? [...new Set([...(x.pages || [x.page]), page])] : x.pages,
                f: { ...x.f, [key]: { ...x.f[key], value, box, page: fPage, conf: 1, edited: true, fb: "fixed", manual: true, learned: null } },
            }));
            return key === "name" || newName ? placeByName(next, id) : next;
        });
        if ((key === "name" || newName) && !itemId) sendAiFeedback(projectId, [{ field: "name", kind: "insert_pos", ai_value: null, value: String(key === "name" ? value : newName), page }], drawingType);
        if (fPage && !isCustomKey(key)) sendAiFeedback(projectId, [{ field: key, kind: "cross_page", ai_value: null, value: String(value), page, position: String(target.f.name?.value ?? "") }], drawingType);
        const nb = target.f.name?.box, loc = locationOf(box);
        let rel = "";
        if (nb && key !== "name" && !fPage) {
            const dx = (box[0] + box[2]) / 2 - (nb[0] + nb[2]) / 2, dy = (box[1] + box[3]) / 2 - (nb[1] + nb[3]) / 2;
            rel = Math.abs(dy) > Math.abs(dx) ? (dy > 0 ? "below" : "above") : (dx > 0 ? "right" : "left");
        }
        if (isCustomKey(key)) {
            addCustomExample(key, { value: String(value), context: context || [], location: rel ? `${rel}-of-pos|${loc}` : loc, position: String(target.f.name?.value ?? newName ?? "") || null, page, project_id: projectId });
            setMarkFor(null);
            toast.success(t("cf_example_saved", { f: t(`ai_f_${key}`) }));
            return;
        }
        sendAiFeedback(projectId, [{ ...feedbackItem({ ...target, f: { ...target.f, [key]: { box } } }, key, "region", value), page, ai_value: null, location: rel ? `${rel}-of-pos|${loc}` : loc }], drawingType);
        setMarkFor(null);
        toast.success(t("al_learned_toast"));
    };
    const renumber = (changes) => {
        const to = new Map(changes.map((c) => [c.id, c.to]));
        setItems((list) => list.map((x) => (to.has(x._id) ? { ...x, f: { ...x.f, name: { ...x.f.name, value: to.get(x._id), edited: true, fb: "fixed", conf: 1, learned: null } } } : x)));
        const learn = changes.map((c) => items.find((x) => x._id === c.id)).filter((it) => it && !it.manual && !it.f.name?.manual && it.f.name?.orig !== "" && it.f.name?.orig != null);
        sendAiFeedback(projectId, learn.map((it) => ({ ...feedbackItem(it, "name", "renumber", to.get(it._id)), ai_value: String(it.f.name.value ?? "") })), drawingType);
        toast.success(t("rn_done", { n: changes.length }));
    };
    // Insert an empty position with this name at index (from the AI table "+" or a gap suggestion).
    const insertAt = (index, name, near) => {
        const _id = Math.max(-1, ...items.map((x) => x._id)) + 1;
        const base = toReviewItems([{ page: near?.page || 1 }])[0];
        const it = { ...base, _id, page: near?.page || 1, manual: true, group: near?.group, block: near?.block || "",
            f: { ...base.f, name: { value: name, box: null, conf: 1, edited: true, fb: "fixed", manual: true }, qty: { ...base.f.qty, conf: 1, edited: true } } };
        setItems((list) => { const l = [...list]; l.splice(Math.min(index, l.length), 0, it); return l; });
        sendAiFeedback(projectId, [{ field: "name", kind: "insert_pos", ai_value: null, value: String(name), page: it.page }], drawingType);
        toast.success(t("ins_created", { name }));
        return _id;
    };
    const move = (id, index) => setItems((list) => moveTo(list, id, index));
    // Sort the rows of one sequence by number into the slots they already occupy (other rows stay in place).
    const reorderSeq = (slots, ids) => setItems((list) => {
        const out = [...list];
        const idx = slots.map((id) => list.findIndex((x) => x._id === id)).filter((i) => i >= 0).sort((a, b) => a - b);
        const rows = ids.map((id) => list.find((x) => x._id === id)).filter(Boolean);
        if (idx.length !== rows.length) return list;
        idx.forEach((ix, i) => { out[ix] = rows[i]; });
        return out;
    });
    const rename = (id, to) => {
        const it = items.find((x) => x._id === id);
        if (!it) return;
        setItems((list) => list.map((x) => (x._id === id ? { ...x, f: { ...x.f, name: { ...x.f.name, value: to, edited: true, fb: "fixed", conf: 1, learned: null } } } : x)));
        sendAiFeedback(projectId, [{ ...feedbackItem(it, "name", "renumber", to), ai_value: String(it.f.name?.value ?? "") }], drawingType);
    };
    // Move a value to another position (box menu "Change position" or drag & drop in the AI table); asks first via pendingMove.
    const askMove = (fromId, key, toId) => { if (fromId !== toId) setPendingMove({ fromId, key, toId }); };
    const doMove = (clear) => {
        const pm = pendingMove;
        setPendingMove(null);
        const src = pm && items.find((x) => x._id === pm.fromId), dst = pm && items.find((x) => x._id === pm.toId);
        if (!src || !dst) return;
        const { key } = pm, f = src.f[key] || {}, bp = f.page || src.page;
        setItems((list) => list.map((x) => {
            if (x._id === dst._id) return { ...x, f: { ...x.f, [key]: { ...x.f[key], value: f.value, box: f.box, page: bp !== dst.page ? bp : undefined, conf: 1, edited: true, fb: "fixed", learned: null, reseq: false } } };
            if (x._id === src._id && clear) return { ...x, f: { ...x.f, [key]: { ...x.f[key], value: key === "qty" ? 1 : "", box: null, page: undefined, conf: 0, edited: true, fb: "removed", learned: null } } };
            return x;
        }));
        const from = String(src.f.name?.value ?? ""), to = String(dst.f.name?.value ?? "");
        if (!f.manual && !isCustomKey(key)) sendAiFeedback(projectId, [{ field: key, kind: "move_pos", ai_value: String(f.value ?? ""), value: String(f.value ?? ""), position: to, from_position: from, page: bp }], drawingType);
        const si = Math.min(items.findIndex((x) => x._id === src._id), items.findIndex((x) => x._id === dst._id));
        setChain((c) => {
            const ci = c ? items.findIndex((x) => x._id === c.startId) : -1;
            return { startId: items[ci >= 0 && ci < si ? ci : si]?._id, fixes: [...(c?.fixes || []), { field: key, value: String(f.value ?? ""), from, to }].slice(-20) };
        });
        toast.success(t("mv_done", { f: t(`ai_f_${key}`), v: f.value, to: to || "—" }));
    };
    return { items, setItems, markFor, setMarkFor, markStay, setMarkStay, markMode, setMarkMode, pendingMove, setPendingMove, askMove, doMove, chain, setChain, focusRow, setFocusRow, flashBox, setFlashBox, reorderSeq, confirm, reject, remove, reassign, addRegion, renumber, insertAt, move, rename, projectId, drawingType };
}
